import * as React from 'react'
import {Box, Typography, TextField, MenuItem, Button, Tooltip, InputAdornment} from '@mui/material'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {useSnackbar} from 'notistack'
import {primaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, MUTED, BORDER, LIGHT_BG, WHITE, PRIMARY_BLUE, SECONDARY_BLUE, SUCCESS, ERROR, withOpacity} from '../../../styles/foundation/colors'
import {fmtKg} from './format'
import {volumetricKg} from './state/volumetric'
import {missingForSubmit, missingSentence, isValidProductUrl} from './state/evidenceIntake'

// One row, one unit each: the three sides the merchant measured plus the
// figure their scale showed. Dead weight sits WITH the dimensions because a
// merchant reads all four off the same parcel in one pass — splitting them
// into two sections asked them to put the box down twice.
const MEASURE_FIELDS = [
  ['l', 'Length', 'cm'],
  ['b', 'Breadth', 'cm'],
  ['h', 'Height', 'cm'],
  ['w', 'Dead weight', 'kg'],
]

// A short, plausible fixed list — this dataset has no real product-taxonomy
// export to draw from (Nobero's own SKUs are all apparel; see
// data/noberoDisputes.js's header), and Product category is optional (C5)
// so the exact set is not load-bearing for anything downstream.
const PRODUCT_CATEGORIES = ['Apparel', 'Footwear', 'Accessories', 'Home & Living', 'Other']

/**
 * The five photo slots, one fact each.
 *
 * Named slots rather than a bare file count, because "5 images attached" and
 * "the five shots that actually prove the case" are different things — a
 * merchant can upload five pictures of the same side of the box and satisfy a
 * counter while proving nothing. Here the slot IS the instruction.
 */
const SHOTS = [
  {id: 'length', label: 'Package length', hint: 'Tape along the long side'},
  {id: 'width', label: 'Package width', hint: 'Tape across the width'},
  {id: 'height', label: 'Package height', hint: 'Tape up the height'},
  {id: 'scale', label: 'Shipment on weighing scale', hint: 'Display readable'},
  {id: 'label', label: 'Package with label', hint: 'AWB barcode in focus'},
]

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const captionSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: MUTED}

// MUI gives .MuiInputBase-input and .MuiSelect-select their own 1rem from
// theme.typography.body1, which beats inheritance — so sizing the root alone
// left every control here rendering at 16px, larger than anything else in the
// module. That extra size is what truncated "Length (cm)" to "Length (c...".
const fieldSx = {
  fontFamily: FONT,
  fontSize: SIZE.xs,
  '& .MuiInputBase-input': {fontFamily: FONT, fontSize: SIZE.xs},
  '& .MuiSelect-select': {fontFamily: FONT, fontSize: SIZE.xs},
  '& .MuiInputLabel-root': {fontFamily: FONT},
  // MUI draws the required asterisk in the label's own colour, which is the
  // same grey as the label text — present but not readable as a marker. This
  // is the only reason the rule exists.
  '& .MuiFormLabel-asterisk': {color: ERROR},
}

const sectionSx = {display: 'grid', gap: 0.75}
// A string is the single sm-and-up template; an object is a breakpoint map for
// rows that need more than one step. Either way xs stays one column per row —
// nothing here survives being quartered on a phone-width drawer.
const fieldRowSx = (cols) => ({
  display: 'grid',
  gridTemplateColumns: typeof cols === 'string' ? {xs: '1fr', sm: cols} : {xs: '1fr', ...cols},
  gap: 1.5,
})
const subHeadingSx = {fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, width: 'fit-content'}

/**
 * A numbered step heading.
 *
 * It used to carry an approval-rate chip beside the title — "Recommended ·
 * 97% approved" and "52% approved". Both were removed: no data in this repo
 * produces either figure, and v1's own copy quotes roughly 100% and 50% for
 * the same two kinds of evidence, so the panel was stating a precision it
 * does not have. The chip support went with them so the next person has to
 * add a source, not just a prop.
 */
function StepHead({n, title}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
      <Box sx={{
        width: 20, height: 20, flexShrink: 0, borderRadius: '6px',
        bgcolor: withOpacity(PRIMARY_BLUE, 0.12), color: SECONDARY_BLUE,
        display: 'grid', placeItems: 'center',
        fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.bold,
      }}>
        {n}
      </Box>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY}}>
        {title}
      </Typography>
    </Box>
  )
}

/* ── The three guidance illustrations ─────────────────────────────
 * Authored inline rather than taken from an icon set: none of these three
 * ideas — a tape laid alongside a carton, a parcel on a scale with a legible
 * display, a barcode on the outer box — exists as a stock glyph, and an
 * approximate icon would illustrate the wrong instruction. One stroke weight,
 * currentColor, so they inherit the card's accent.
 */
const artSx = {width: 52, height: 44}
const svgProps = {
  viewBox: '0 0 48 40',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.4,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

function MeasureInFrame() {
  return (
    <Box component='svg' {...svgProps} sx={artSx} aria-hidden='true'>
      {/* carton in perspective */}
      <path d='M10 12 L26 6 L40 11 L24 17 Z' />
      <path d='M10 12 L10 26 L24 31 L24 17' />
      <path d='M40 11 L40 25 L24 31' />
      {/* a tape measure laid alongside the edge being measured, with ticks */}
      <path d='M8 35 L28 35' />
      <path d='M8 33 L8 37 M13 34 L13 36 M18 33.5 L18 36.5 M23 34 L23 36 M28 33 L28 37' />
    </Box>
  )
}

function WeightLegible() {
  return (
    <Box component='svg' {...svgProps} sx={artSx} aria-hidden='true'>
      {/* the sealed parcel */}
      <path d='M14 6 L30 6 L30 18 L14 18 Z' />
      <path d='M22 6 L22 18' />
      {/* scale platform and body, with a readable display */}
      <path d='M8 20 L38 20' />
      <path d='M11 20 L11 30 L35 30 L35 20' />
      <rect x='18' y='23.5' width='10' height='4' rx='1' />
      <path d='M13 33 L33 33' />
    </Box>
  )
}

function LabelVisible() {
  return (
    <Box component='svg' {...svgProps} sx={artSx} aria-hidden='true'>
      {/* the label, barcode in focus */}
      <rect x='8' y='11' width='26' height='18' rx='2' />
      <path d='M13 16 L13 24 M16 16 L16 24 M19 16 L19 24 M22 16 L22 24 M25 16 L25 24 M28 16 L28 24' />
      {/* the outer packaging it is stuck to */}
      <path d='M34 15 L40 15 M34 20 L38 20 M34 25 L40 25' />
    </Box>
  )
}

const WINNING_SET = [
  {n: 1, title: 'Measure in frame', Art: MeasureInFrame,
    body: 'Tape or scale laid alongside the side being measured, both readable in one shot.'},
  {n: 2, title: 'Weight legible', Art: WeightLegible,
    body: 'The sealed parcel on the scale with the display digits sharp — no hands, no glare.'},
  {n: 3, title: 'Label visible', Art: LabelVisible,
    body: 'AWB barcode in focus on the outer packaging, tying the parcel to this shipment.'},
]

/**
 * Single-record evidence-capture flow.
 *
 * NOT collapsible. It used to be an Accordion, on the reasoning that the
 * drawer's default view should stay compact — but attaching evidence is the one
 * thing this drawer exists to let a merchant DO, and hiding the whole flow
 * behind a disclosure made the drawer's primary action its least visible
 * element.
 *
 * The two evidence steps are numbered because they are genuinely ordered by
 * strength: one packing video settles a dispute on its own, and the five-shot
 * photo set is the fallback when no video was taken. The numbers are a claim
 * about evidence strength, not a position in the form — which is why they still
 * read 1 and 2 even though the measurements and product details now sit ABOVE
 * them rather than below. That order was changed on request, and it is the
 * better one: the measurements ARE the claim being made, and the evidence is
 * what argues for it, so the form now states the figures before it proves them.
 *
 * `row` is the drawer's live row (index.js's mutable state, not the frozen
 * DISPUTES export). `onSubmit(rowId, payload)` is how the caller applies the
 * change to its own state — the same local-only contract EvidenceStudio.js
 * uses; this component never mutates `row` itself.
 *
 * Evidence is tracked as slot flags and a count, exactly as EvidenceStudio.js's
 * upload area already does. The one object URL is ShotPreview's, below: it is
 * created per chosen photo and revoked when the photo changes or the slot goes.
 */

const isBlob = (f) => typeof Blob !== 'undefined' && f instanceof Blob

/**
 * The chosen photo itself on its slot, so the merchant can see which shot went
 * where — five slots that all said "Attached" could not tell them that.
 */
function ShotPreview({file, isVideo = false, height = 72}) {
  const [src, setSrc] = React.useState(null)
  React.useEffect(() => {
    if (!isBlob(file) || typeof URL.createObjectURL !== 'function') { setSrc(null); return undefined }
    const u = URL.createObjectURL(file)
    setSrc(u)
    return () => URL.revokeObjectURL(u)
  }, [file])
  if (!src) return null
  const sx = {width: '100%', height, objectFit: 'cover', borderRadius: '6px', border: `1px solid ${BORDER}`, bgcolor: isVideo ? NAVY : WHITE}
  return isVideo
    ? <Box component='video' src={src} muted playsInline preload='metadata' sx={sx} />
    : <Box component='img' src={src} alt='' sx={sx} />
}

export default function EvidenceIntake({row, onSubmit}) {
  const {enqueueSnackbar} = useSnackbar()
  const [measure, setMeasure] = React.useState({l: '', b: '', h: '', w: ''})
  const [shots, setShots] = React.useState({})
  const [video, setVideo] = React.useState(false)
  const [dragging, setDragging] = React.useState(null)
  const [category, setCategory] = React.useState('')
  const [url, setUrl] = React.useState('')

  const l = Number(measure.l)
  const b = Number(measure.b)
  const h = Number(measure.h)
  const w = Number(measure.w)
  const dimsTyped = l > 0 && b > 0 && h > 0
  const weightTyped = w > 0
  const liveVol = dimsTyped ? volumetricKg(l, b, h) : null
  // The merchant's own figures may not come to more than the courier billed: a parcel heavier than
  // the charge is not a dispute, it is an Accept. The server refuses the same figures.
  const billedKg = Number(row.appliedWeight) || 0
  const claimedKg = Math.max(weightTyped ? w : 0, liveVol || 0)
  const overBilled = billedKg > 0 && claimedKg > billedKg + 0.005
  const overBilledMsg = overBilled
    ? `Your figures come to ${fmtKg(claimedKg)}, more than the ${fmtKg(billedKg)} the courier billed. Accept the charge instead of disputing it.`
    : ''

  // The submit payload carries a COUNT of images, so the named slots stay a UI
  // affordance and state/evidenceIntake.js needs no new shape.
  const filledShots = SHOTS.filter((sh) => shots[sh.id]).length

  const missing = missingForSubmit({
    dimsTyped, weightTyped, hasImages: filledShots > 0, hasVideo: video,
  })
  // The same sentence the drawer footer's blocked Dispute button prints, from
  // the same builder — the two controls sit in one drawer and must not phrase
  // one rule two ways.
  const stillNeeded = missingSentence(missing)
  const blocked = missing.length > 0 || overBilled
  const blockedReason = missing.length > 0 ? stillNeeded : overBilledMsg
  const urlValid = isValidProductUrl(url)

  // The File object itself is kept in state (truthy, so every boolean check
  // above is unchanged) — it is what the live submit path actually uploads.
  // Drag-drop and picker paths both land here; a source that supplies no File
  // (the harness's synthetic drops) falls back to `true`, which the API seam
  // skips, preserving the old flag-only behaviour. Still no object URLs.
  const takeVideo = (files) => { if (files.length > 0) setVideo(files[0] || true) }
  const takeShot = (id, files) => { if (files.length > 0) setShots((s) => ({...s, [id]: files[0] || true})) }

  // The copy says "drop it here", so it has to actually accept a drop —
  // otherwise the affordance is a lie. preventDefault on dragOver is what stops
  // the browser navigating away to the dropped file instead.
  const dropHandlers = (key, take) => ({
    onDragOver: (e) => { e.preventDefault(); setDragging(key) },
    onDragLeave: () => setDragging((d) => (d === key ? null : d)),
    onDrop: (e) => {
      e.preventDefault()
      setDragging(null)
      take(Array.from((e.dataTransfer && e.dataTransfer.files) || []))
    },
  })

  const handleSubmit = async () => {
    if (blocked) return
    // `files` rides along for the live submit path (data/api.js) — the local
    // demo path (state/evidenceIntake.js applyEvidenceIntake) reads only the
    // count/flag fields it always has. An onSubmit that resolves to `false`
    // is a failed live upload: the caller has already toasted the reason, and
    // the form keeps its state so the merchant can retry without re-attaching.
    const ok = await Promise.resolve(onSubmit(row.id, {
      addImages: filledShots,
      addVideo: !!video,
      dim: dimsTyped ? {l, b, h} : null,
      weightKg: weightTyped ? w : null,
      productCategory: category || null,
      productUrl: url.trim() || null,
      files: {images: {...shots}, video},
    }))
    if (ok === false) return
    enqueueSnackbar(`Evidence attached to AWB ${row.awb}`, {variant: 'success'})
    // The figures stay: they are what was just saved, and clearing them read as the dispute having
    // thrown the merchant's input away. Only the files go — they are on the record now (Your
    // evidence, above) and would upload a second time if left attached.
    setShots({})
    setVideo(false)
  }

  return (
    <Box sx={{border: `1px solid ${BORDER}`, borderRadius: '10px', p: 2, display: 'grid', gap: 2}}>
      {/* ── Dimensions & dead weight ───────────────────────────── */}
      <Box sx={sectionSx}>
        <Typography sx={subHeadingSx}>Dimensions &amp; dead weight</Typography>
        {/* Four across at md, where the drawer is 780px wide and each field
            gets ~164px. Not at sm: the drawer is 560px there, which quarters
            to 112px and clips the "Dead weight" label — measured, the same
            truncation fieldSx's own note was written about. xs stacks. */}
        <Box sx={fieldRowSx({sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)'})}>
          {MEASURE_FIELDS.map(([k, label, unit]) => (
            <TextField
              key={k}
              required
              size='small' label={label} value={measure[k]}
              onChange={(e) => setMeasure((m) => ({...m, [k]: e.target.value}))}
              inputProps={{inputMode: 'decimal'}}
              InputProps={{endAdornment: <InputAdornment position='end'><Typography sx={labelSx}>{unit}</Typography></InputAdornment>}}
              sx={fieldSx}
            />
          ))}
        </Box>
        <Typography sx={dimsTyped ? {...labelSx, color: NAVY, fontWeight: WEIGHT.semibold} : labelSx}>
          {dimsTyped
            ? `Volumetric from these dimensions: ${fmtKg(liveVol)}`
            : 'All four are required — a dispute has to state the figures it is arguing for.'}
        </Typography>
        {overBilled && (
          <Typography role='alert' sx={{...labelSx, color: ERROR, fontWeight: WEIGHT.semibold}}>{overBilledMsg}</Typography>
        )}
      </Box>

      {/* ── Product details ────────────────────────────────────── */}
      <Box sx={sectionSx}>
        <Typography sx={subHeadingSx}>
          Product details <Box component='span' sx={{...labelSx, fontWeight: WEIGHT.regular}}>· optional</Box>
        </Typography>
        <Box sx={fieldRowSx('1fr 1fr')}>
          <TextField
            select size='small' label='Product category' value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={fieldSx}
          >
            <MenuItem value=''><em>None</em></MenuItem>
            {PRODUCT_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField
            size='small' label='Product URL' value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={!urlValid}
            helperText={urlValid ? '' : 'Enter a valid URL, e.g. https://example.com/product.'}
            sx={fieldSx}
          />
        </Box>
      </Box>

      {/* The asterisk sits on the GROUP, not on either step. The rule is a
          photo OR a video, so marking both would say both are required and
          marking one would say the other is optional — neither is true. The
          line beneath states the rule in words, because an asterisk alone
          cannot express an OR. */}
      <Box>
        <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY}}>
          Add evidence <Box component='span' aria-hidden='true' sx={{color: ERROR}}>*</Box>
        </Typography>
        <Typography sx={labelSx}>Give a packing video or photo evidence — either one is enough.</Typography>
      </Box>

      {/* ── 1. Packing video ───────────────────────────────────── */}
      <Box sx={sectionSx}>
        <StepHead n={1} title='Packing video' />
        <Box
          component='label'
          {...dropHandlers('video', takeVideo)}
          sx={{
            display: 'grid', placeItems: 'center', gap: 0.5, textAlign: 'center',
            border: `1px dashed ${video ? SUCCESS : PRIMARY_BLUE}`,
            bgcolor: withOpacity(video ? SUCCESS : PRIMARY_BLUE, dragging === 'video' ? 0.12 : 0.05),
            borderRadius: '10px', px: 2, py: 2.5, cursor: 'pointer',
            '&:focus-within': {outline: `2px solid ${SECONDARY_BLUE}`, outlineOffset: '2px'},
          }}
        >
          {isBlob(video) ? (
            <Box sx={{width: 220, maxWidth: '100%'}}><ShotPreview file={video} isVideo height={124} /></Box>
          ) : (
            <Box sx={{width: 40, height: 40, borderRadius: '50%', bgcolor: WHITE, display: 'grid', placeItems: 'center', color: video ? SUCCESS : SECONDARY_BLUE}}>
              {video ? <CheckCircleIcon /> : <VideocamOutlinedIcon />}
            </Box>
          )}
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY}}>
            {video ? `Packing video attached${isBlob(video) && video.name ? ` · ${video.name}` : ''}` : 'Drop your packing video here'}
          </Typography>
          <Typography sx={captionSx}>MP4, MOV or WEBM · up to 100 MB · 20–90 seconds is plenty</Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, color: SECONDARY_BLUE}}>
            <FileUploadOutlinedIcon sx={{fontSize: 15}} />
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: 'inherit'}}>
              {video ? 'Replace file' : 'Browse files'}
            </Typography>
          </Box>
          <input
            type='file' hidden accept='video/*'
            aria-label='Attach packing video'
            onChange={(e) => { takeVideo(Array.from(e.target.files || [])); e.target.value = '' }}
          />
        </Box>
      </Box>

      {/* ── 2. Photo evidence ──────────────────────────────────── */}
      <Box sx={sectionSx}>
        <StepHead n={2} title='Photo evidence' />
        <Typography sx={{...labelSx, lineHeight: 1.5}}>
          Five shots, one fact each. Keep the measuring device and the parcel in the same frame — a
          number with no parcel beside it proves nothing.
        </Typography>
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'}, gap: 1}}>
          {SHOTS.map((sh) => {
            const done = Boolean(shots[sh.id])
            return (
              <Box
                key={sh.id}
                component='label'
                {...dropHandlers(sh.id, (files) => takeShot(sh.id, files))}
                sx={{
                  display: 'grid', placeItems: 'center', gap: 0.25, textAlign: 'center',
                  border: `1px ${done ? 'solid' : 'dashed'} ${done ? SUCCESS : BORDER}`,
                  bgcolor: dragging === sh.id
                    ? withOpacity(PRIMARY_BLUE, 0.08)
                    : (done ? withOpacity(SUCCESS, 0.05) : LIGHT_BG),
                  borderRadius: '10px', p: 1.5, cursor: 'pointer',
                  '&:hover': {borderColor: done ? SUCCESS : SECONDARY_BLUE},
                  '&:focus-within': {outline: `2px solid ${SECONDARY_BLUE}`, outlineOffset: '2px'},
                }}
              >
                {done && isBlob(shots[sh.id]) ? (
                  <ShotPreview file={shots[sh.id]} />
                ) : (
                  <Box sx={{width: 28, height: 28, borderRadius: '50%', bgcolor: WHITE, display: 'grid', placeItems: 'center', color: done ? SUCCESS : MUTED}}>
                    {done ? <CheckCircleIcon sx={{fontSize: 18}} /> : <ImageOutlinedIcon sx={{fontSize: 17}} />}
                  </Box>
                )}
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY}}>
                  {sh.label}
                </Typography>
                <Typography sx={captionSx}>{sh.hint}</Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.4, color: done ? SUCCESS : SECONDARY_BLUE, mt: 0.25}}>
                  {!done && <FileUploadOutlinedIcon sx={{fontSize: 13}} />}
                  <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.bold, color: 'inherit'}}>
                    {done ? 'Attached · click to replace' : 'Upload or drop'}
                  </Typography>
                </Box>
                <input
                  type='file' hidden accept='image/*' multiple
                  aria-label={`Attach ${sh.label.toLowerCase()} — ${sh.hint}`}
                  onChange={(e) => { takeShot(sh.id, Array.from(e.target.files || [])); e.target.value = '' }}
                />
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* ── What a winning set looks like ──────────────────────── */}
      <Box sx={{bgcolor: LIGHT_BG, border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.75}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, color: SECONDARY_BLUE}}>
          <InfoOutlinedIcon sx={{fontSize: 16}} />
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY}}>
            What a winning photo set looks like
          </Typography>
        </Box>
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'}, gap: 1.25}}>
          {WINNING_SET.map(({n, title, body, Art}) => (
            <Box key={n} sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '8px', p: 1.25}}>
              <Box sx={{color: SECONDARY_BLUE, mb: 0.5}}><Art /></Box>
              <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY}}>
                {n} · {title}
              </Typography>
              <Typography sx={{...captionSx, lineHeight: 1.5, mt: 0.25}}>{body}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* The form's one structural rule, separating the action from the fields
          it commits. The running tally sits beside it so a merchant can see how
          much of the set is in before they submit. */}
      <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, borderTop: `1px solid ${BORDER}`, pt: 2}}>
        {(filledShots > 0 || video) && (
          <Typography sx={captionSx}>
            {filledShots} of {SHOTS.length} shots{video ? ' · video attached' : ''}
          </Typography>
        )}
        <Tooltip title={blockedReason}>
          {/* A disabled MUI Button swallows hover and takes no focus — this
              span wrapper is what lets keyboard/AT users still reach the
              tooltip. role='note' and the aria-label are what make the reason
              audible once focus lands, the same shape SkuFreezeDrawer.js's
              blocked Request button and the drawer footer's blocked Dispute
              button use; they are omitted while the button is live, where the
              button speaks for itself. */}
          <Box
            component='span'
            tabIndex={blocked ? 0 : -1}
            role={blocked ? 'note' : undefined}
            aria-label={blocked ? `Submit evidence is unavailable. ${blockedReason}` : undefined}
            sx={{display: 'inline-flex'}}
          >
            <Button
              variant='contained'
              disabled={blocked}
              onClick={handleSubmit}
              aria-label={blocked ? `Submit evidence. ${blockedReason}` : 'Submit evidence'}
              sx={{...primaryBtnSx, bgcolor: SECONDARY_BLUE, '&:hover': {bgcolor: SECONDARY_BLUE, filter: 'brightness(0.92)'}}}
            >
              Submit evidence
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  )
}
